import { CAMINO } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { rngInt, seedRng, deriveSeed } from '../math/rng.ts';
import type { GuardRules, HermitRules, LitterRules, VelaRules } from '../types.ts';
import { base } from './litter-rules.ts';
import type { NaveLayout } from './ermita-layout.ts';
import { brazierBy, renaveStart, vigilPlaces, type AtriumLayout } from './vigilia-layout.ts';

/**
 * The stretcher's rules for the cleric's chapter 5 (docs/sendas/clerigo.md, «La vigilia»; content CAMINO.lamps,
 * procession, renave, mourn), from chapter 4's nave (the one won, battle/ermita-layout.ts) or the atrium
 * (battle/vigilia-layout.ts): the fight's builder (campaign/sendas.ts) and the renderer's scenes build them the same way.
 * Lives in hit points worked out by the caller (seconds of D).
 */

const NEVER = 0x7fffffff;

const board = (L: NaveLayout, seed: number): Omit<HermitRules, 'vigil' | 'nave'> => ({
  lantern: { ...L.lantern },
  altar: { ...L.altar },
  door: { ...L.door },
  mirrors: L.mirrors.map((m, i) => ({ q: m.q, r: m.r, stand: { ...(L.mirrorStand[i] as { x: number; y: number }) } })),
  splits: L.splits.map((m, i) => ({ q: m.q, r: m.r, stand: { ...(L.splitStand[i] as { x: number; y: number }) } })),
  ink: L.ink.map((p) => ({ ...p })),
  cells: L.cells.map((p) => ({ ...p })),
  atrium: { ...L.atrium },
  stand: CAMINO.hermit.stand,
  reach: CAMINO.hermit.reach,
  naveSeed: seed,
});

const shell = (L: { x: number; y: number }, kind: LitterRules['kind']): Omit<LitterRules, 'hermit'> => ({
  ...base(),
  kind,
  way: [{ ...L }],
  dens: [],
  foes: null,
  crows: null,
  limpet: null,
  mirrors: null,
});

const vela = (L: NaveLayout, kind: VelaRules['kind']): VelaRules => {
  const P = vigilPlaces(L);
  return { kind, lamps: L.lamps.map((p) => ({ ...p })), stands: P.stands, candle: P.candle, keeps: P.keeps, roof: P.roof, away: P.away, lamp: null, procession: null, mourn: null };
};

const guard = (g: { every: number; reach: number; guard: number; speed: number }, hit: number): GuardRules => ({ every: g.every, hit, reach: g.reach, guard: g.guard, speed: g.speed });

/** Act 1: the lamps of the vigil, in chapter 4's nave (its seed `seed`). */
export const lampsRules = (L: NaveLayout, seed: number, life: { limpet: number; ring: number }): LitterRules => {
  const A = CAMINO.lamps;
  const v = vela(L, 'lamps');
  v.lamp = {
    every: A.every,
    light: A.light,
    ring: A.ring,
    ringHp: life.ring,
    hits: A.hits,
    blow: A.blow,
    limpet: { hp: life.limpet, speed: A.limpet.speed, first: A.limpet.first, every: A.limpet.every, cap: A.limpet.cap, snuff: A.limpet.snuff },
    dead: { first: A.dead.first, every: A.dead.every, n: [A.dead.n[0], A.dead.n[1]] },
    garg: { ...A.garg },
    singer: A.singer,
    settle: A.settle,
  };
  return { ...shell(L.rest, 'lamps'), hermit: { ...board(L, seed), vigil: null, nave: null, vela: v } };
};

/** Act 2: the procession in the atrium. Joran's and his apprentices' blow in hit points. */
export const processionRules = (A: AtriumLayout, hit: number): LitterRules => {
  const P = CAMINO.procession;
  const v: VelaRules = {
    kind: 'procession',
    lamps: [],
    stands: [],
    candle: { ...A.door },
    keeps: A.keeps.map((k) => ({ ...k })),
    roof: { x: 0, y: 0 },
    away: { ...A.gate },
    lamp: null,
    procession: {
      door: { ...A.door },
      gate: { ...A.gate },
      doorHits: P.door,
      blow: P.blow,
      bite: P.bite,
      band: { first: P.band.first, every: P.band.every, until: P.band.until, n: [P.band.n[0], P.band.n[1]] },
      singerEvery: P.singerEvery,
      mournerEvery: P.mournerEvery,
      guard: guard(P.guard, hit),
      lift: P.lift,
      liftBp: P.liftBp,
      settle: P.settle,
    },
    mourn: null,
  };
  const d = hexToWorld(A.doorHex.q, A.doorHex.r);
  return {
    ...shell(A.start, 'procession'),
    hermit: {
      lantern: { q: 0, r: 0, dir: 0 },
      altar: { ...A.doorHex },
      door: { ...A.doorHex },
      mirrors: [],
      splits: [],
      ink: [],
      cells: A.cells.map((p) => ({ ...p })),
      atrium: { ...A.gate },
      stand: CAMINO.hermit.stand,
      reach: CAMINO.hermit.reach,
      vigil: null,
      nave: null,
      vela: { ...v, candle: { x: d.x, y: d.y } },
    },
  };
};

const naveOf = (L: NaveLayout, start: { turns: number[]; splits: number[] }, life: { limpet: number; burn: number }, lim: { trickle: number; cap: number; first: number; mirrorBp: number }, dead: { first: number; every: number; n: [number, number]; grow: number }, hold: number, hail?: { first: number; every: number; warn: number; bp: number }): NonNullable<HermitRules['nave']> => {
  const N = CAMINO.nave;
  return {
    answer: { turns: [...L.answer.turns], splits: [...L.answer.splits] },
    start: [...start.turns],
    split0: [...start.splits],
    aud: L.aud,
    stone: { ...L.stone },
    take: N.take,
    hold,
    set: true,
    burn: life.burn,
    limpet: { hp: life.limpet, speed: N.limpet.speed, drainBp: N.limpet.drainBp, heroBp: N.limpet.heroBp, trickle: lim.trickle, cap: lim.cap, first: lim.first, mirrorBp: lim.mirrorBp, leap: N.limpet.leap },
    singer: 0,
    dirge: hail ? { ...hail } : { first: NEVER, every: NEVER, warn: 0, bp: 0 },
    dead,
  };
};

/** The stretcher's limpets (Tessa's): their drain and leap; out of the walls by battle/ermita.ts or vigilia.ts, not at bends. */
const tessaLimpets = (hp: number): NonNullable<LitterRules['limpet']> => {
  const N = CAMINO.nave.limpet;
  return { hp, speed: N.speed, drainBp: N.drainBp, heroBp: N.heroBp, burst: 0, near: 0, trickle: NEVER, cap: 12, leap: N.leap, bends: [] };
};

/** Act 3, the test: the mirrors again, three moved by the wind (`fightSeed` says which), Aud's in its place. */
export const renaveRules = (L: NaveLayout, seed: number, fightSeed: number, life: { limpet: number; burn: number }): LitterRules => {
  const R = CAMINO.renave;
  const start = renaveStart(L, fightSeed, R.moved);
  return {
    ...shell(L.rest, 'nave'),
    limpet: tessaLimpets(life.limpet),
    // Soot crows down by the hole in the roof, at the hero (battle/litter.ts's flocks).
    crows: { first: R.crows.first, every: R.crows.every, n: R.crows.n, at: 'hero' },
    hermit: {
      ...board(L, seed),
      vigil: null,
      nave: naveOf(L, start, life, R.limpet, { first: R.dead.first, every: R.dead.every, n: [R.dead.n[0], R.dead.n[1]], grow: R.dead.grow }, R.hold, R.hail),
    },
  };
};

/**
 * Act 4: the Doliente. Her life, her tongues', the beam's burns and the guards' blow in hit points; the decision of
 * chapter 4; her scars (each slows her tongues).
 */
export const mournRules = (
  L: NaveLayout,
  seed: number,
  fightSeed: number,
  life: { boss: number; tongue: number; hush: number; limpet: number; burn: number; lantern: number; sunTongue: number; sun: number; guard: number },
  believe: boolean,
  scars: number,
): LitterRules => {
  const M = CAMINO.mourn;
  const v = vela(L, 'mourn');
  // The frosted mirror: one of the way's (not Aud's), by the seed.
  const way: number[] = [];
  for (let i = 0; i < 6; i++) if ((L.answer.touched >> i) & 1 && i !== L.aud) way.push(i);
  const rng = seedRng(deriveSeed(fightSeed, 0xf205));
  const fm = way.length ? (way[rngInt(rng, way.length)] as number) : L.aud;
  v.mourn = {
    hp: life.boss,
    glide: M.glide,
    keep: M.keep,
    veil: { ...M.veil },
    hug: { ...M.hug },
    wail: { first: M.wail.first, every: M.wail.every, every2: M.wail.every2, warn: M.wail.warn, long: M.wail.long, hush: life.hush, mute: M.wail.mute, mutedBp: M.wail.mutedBp, sing: M.wail.sing },
    limpets: M.limpets,
    tongue: { hp: life.tongue, speed: M.tongue.speed, gap: M.tongue.gap, snuff: believe ? M.tongue.believe : M.tongue.snuff, fast: M.tongue.fast, nervous: M.tongue.nervous, lantern: life.lantern, sun: life.sunTongue, hold: { ...M.tongue.hold } },
    sunHp: life.sun,
    split: M.split,
    vinegar: M.vinegar,
    frost: { at: M.frost.at, thaw: M.frost.thaw, mirror: fm, brazier: brazierBy(L, fm) },
    sun: M.sun,
    hunt: M.hunt,
    dawn: M.dawn,
    dead: { first: M.dead.first, every: M.dead.every, n: [M.dead.n[0], M.dead.n[1]] },
    bite: M.bite,
    lift: M.lift,
    liftBp: M.liftBp,
    guard: guard(M.guard, life.guard),
    believe,
    slowBp: M.tongue.scarBp * scars,
    lanes: vigilPlaces(L).lanes,
  };
  return {
    ...shell(L.rest, 'mourn'),
    limpet: tessaLimpets(life.limpet),
    hermit: {
      ...board(L, seed),
      vigil: null,
      nave: naveOf(L, { turns: L.answer.turns, splits: L.answer.splits }, { limpet: life.limpet, burn: life.burn }, { trickle: NEVER, cap: 12, first: NEVER, mirrorBp: 0 }, { first: NEVER, every: NEVER, n: [0, 0], grow: 0 }, NEVER),
      vela: v,
    },
  };
};
